"use client";

import { c as _c } from "react/compiler-runtime";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerPortal, DrawerTitle } from "@/components/ui/drawer";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import React from "react";
import { useTranslations } from "next-intl";
import { Button } from "@components/ui/button";
import { SidePanelTabs } from "./side-panel-tabs";
import { SidePanelWrapperProps } from "./types";

/**
 * Maps panel size to Tailwind width classes
 */
const SIZE_CLASSES = {
  sm: "w-[50vw] lg:w-[30vw]",
  md: "w-[50vw]",
  lg: "w-[83vw]",
  xl: "w-[83vw]"
} as const;

/**
 * Core wrapper component that renders individual panels using Sheet or Drawer
 * Adapts between desktop (Sheet) and mobile (Drawer) based on viewport width
 *
 * Performance: Lazy loads portal/content - only mounts when a panel is actually open
 */
export const SidePanelWrapper: React.FC<SidePanelWrapperProps> = t0 => {
  const $ = _c(27);
  const {
    isOpen,
    isTop,
    zIndex,
    title,
    description,
    size,
    onClose,
    children,
    overlayClassName
  } = t0;
  const tCommon = useTranslations("common");
  const isMobile = useIsMobile();
  if (isMobile && !isTop) {
    return null;
  }
  let t1;
  if ($[0] !== onClose) {
    t1 = open => {
      if (!open) {
        onClose();
      }
    };
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleOpenChange = t1;
  if (isMobile) {
    let t2;
    if ($[2] !== children || $[3] !== description || $[4] !== isOpen || $[5] !== onClose || $[6] !== overlayClassName || $[7] !== tCommon || $[8] !== title || $[9] !== zIndex) {
      t2 = isOpen && <DrawerPortal><DrawerContent className="bg-card" style={{
          zIndex
        }} overlayClassName={cn("fixed inset-0 bg-black/40 z-10", overlayClassName)}><DrawerHeader className="text-left"><DrawerTitle>{title}</DrawerTitle></DrawerHeader><DrawerDescription className="px-2 pb-2 text-sm text-muted-foreground">{description}</DrawerDescription><div className="overflow-y-auto px-2 pb-2">{children}</div><DrawerFooter className="pt-2"><DrawerClose asChild={true}><Button variant="outline" onClick={onClose}>{tCommon("words.verbs.close")}</Button></DrawerClose></DrawerFooter></DrawerContent></DrawerPortal>;
      $[2] = children;
      $[3] = description;
      $[4] = isOpen;
      $[5] = onClose;
      $[6] = overlayClassName;
      $[7] = tCommon;
      $[8] = title;
      $[9] = zIndex;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    let t3;
    if ($[11] !== handleOpenChange || $[12] !== isOpen || $[13] !== t2) {
      t3 = <Drawer open={isOpen} onOpenChange={handleOpenChange}>{t2}</Drawer>;
      $[11] = handleOpenChange;
      $[12] = isOpen;
      $[13] = t2;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    return t3;
  }
  let t2;
  if ($[15] !== children || $[16] !== description || $[17] !== isOpen || $[18] !== overlayClassName || $[19] !== size || $[20] !== title || $[21] !== zIndex) {
    t2 = isOpen && <SheetContent side="right" className={cn("flex flex-col sm:max-w-full", SIZE_CLASSES[size])} style={{
      zIndex
    }} overlayClassName={cn("z-10", overlayClassName)}><div className="relative flex h-full flex-col"><SheetHeader className="sticky top-0 border-b bg-card p-4"><SheetTitle>{title}</SheetTitle>{description && <SheetDescription>{description}</SheetDescription>}</SheetHeader><div className="mb-2 flex-1 overflow-y-auto">{children}</div><div className="sticky bottom-0 z-20 border-t p-2" /><SidePanelTabs /></div></SheetContent>;
    $[15] = children;
    $[16] = description;
    $[17] = isOpen;
    $[18] = overlayClassName;
    $[19] = size;
    $[20] = title;
    $[21] = zIndex;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== handleOpenChange || $[24] !== isOpen || $[25] !== t2) {
    t3 = <Sheet open={isOpen} onOpenChange={handleOpenChange}>{t2}</Sheet>;
    $[23] = handleOpenChange;
    $[24] = isOpen;
    $[25] = t2;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  return t3;
};